import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useMeta, useCreateGenre } from '../api/useManga'
import { useAdminManga, useCreateManga, useUpdateManga, useDeleteManga, useAdminChapters, useAdminVolumes, useDeleteChapter, useUploadCover } from '../api/useAdmin'
import { mediaUrl } from '../utils/media'
import Input from '../components/ui/Input'
import ConfirmModal from '../components/ui/ConfirmModal'
import NotFound from './NotFound'
import { getErrMsg } from '../api/client'
import toast from 'react-hot-toast'

const CURRENT_YEAR = new Date().getFullYear()
const EMPTY = { title: '', title_en: '', title_original: '', slug: '', type: '', author: '', year: String(CURRENT_YEAR), genres: [], status: 'ongoing', age_rating: 'safe', description: '' }

const selectCls = 'px-3 py-2.5 rounded-lg text-sm outline-none bg-surface2 border border-line text-fg focus:border-accent transition-colors'

export default function AdminMangaForm() {
  const { id } = useParams()
  const isEdit = !!id
  const navigate = useNavigate()

  const { data: manga, isError, error } = useAdminManga(id)
  const { data: chapters = [], isLoading: loadingChapters } = useAdminChapters(id)
  const { data: volumes = [] } = useAdminVolumes(id)
  const { mutate: create, isPending: creating } = useCreateManga()
  const { mutate: update, isPending: updating } = useUpdateManga(id)
  const { mutate: deleteManga, isPending: deleting } = useDeleteManga()
  const { data: meta } = useMeta()
  const GENRES = meta?.genres ?? []
  const STATUS_OPTIONS = (meta?.statuses ?? []).map((s) => ({ value: s.code, label: s.name }))
  const TYPE_OPTIONS = [
    { value: '', label: '— Не указан —' },
    ...(meta?.types ?? []).map((t) => ({ value: t.code, label: t.name })),
  ]
  const AGE_RATING_OPTIONS = (meta?.age_ratings ?? []).map((r) => ({ value: r.code, label: r.name }))

  const { mutateAsync: uploadCoverAsync } = useUploadCover()

  const [form, setForm] = useState(EMPTY)
  const [coverFile, setCoverFile] = useState(null)
  const [coverPreview, setCoverPreview] = useState(null)
  const [formError, setFormError] = useState('')
  const [sortDesc, setSortDesc] = useState(true)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [newGenre, setNewGenre] = useState('')
  const [showGenreInput, setShowGenreInput] = useState(false)
  const newGenreRef = useRef(null)
  const { mutate: createGenre, isPending: creatingGenre } = useCreateGenre()

  const showVolumeHeaders = volumes.length > 1

  useEffect(() => {
    if (manga && isEdit && meta) {
      const ageRatingCode = (meta.age_ratings ?? []).find((r) => r.name === manga.age_rating)?.code || ''
      setForm({
        title: manga.title || '',
        title_en: manga.title_en || '',
        title_original: manga.title_original || '',
        slug: manga.slug || '',
        type: manga.type || '',
        author: manga.author || '',
        year: manga.year || '',
        genres: (manga.genres || []).map((g) => g.code ?? g),
        status: manga.status || 'ongoing',
        age_rating: ageRatingCode,
        description: manga.description || '',
      })
      if (manga.cover_url) setCoverPreview(mediaUrl(manga.cover_url))
    }
  }, [manga, isEdit, meta])

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const handleAddGenre = () => {
    const name = newGenre.trim()
    if (!name) return
    createGenre(name, {
      onSuccess: (data) => {
        setForm((f) => ({ ...f, genres: [...f.genres, data.code] }))
        setNewGenre('')
        setShowGenreInput(false)
      },
      onError: (err) => {
        if (err?.response?.status === 409) {
          const existing = (meta?.genres ?? []).find(g => g.name === name)
          if (existing) setForm((f) => f.genres.includes(existing.code) ? f : { ...f, genres: [...f.genres, existing.code] })
          setNewGenre('')
          setShowGenreInput(false)
        } else {
          toast.error(getErrMsg(err, 'Ошибка создания жанра'))
        }
      },
    })
  }

  const handleCover = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setCoverFile(file)
    setCoverPreview(URL.createObjectURL(file))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.title.trim()) { setFormError('Укажите название манги'); return }
    setFormError('')
    const payload = {
      title: form.title.trim(),
      title_en: form.title_en.trim() || null,
      title_original: form.title_original.trim() || null,
      type: form.type || null,
      author: form.author.trim() || null,
      year: form.year ? parseInt(form.year) : null,
      status: form.status,
      age_rating: form.age_rating || null,
      description: form.description.trim() || null,
      genres: form.genres,
    }
    if (isEdit && form.slug.trim() && form.slug.trim() !== manga?.slug) payload.slug = form.slug.trim()

    const mutate = isEdit ? update : create
    mutate(payload, {
      onSuccess: async (data) => {
        const savedId = isEdit ? id : data.id
        if (coverFile) {
          try { await uploadCoverAsync({ mangaId: savedId, file: coverFile }) }
          catch (err) { toast.error('Обложка не загружена — сохраните мангу и попробуйте снова') }
        }
        toast.success(isEdit ? 'Сохранено' : 'Манга создана')
        navigate(isEdit ? `/admin/manga/${savedId}` : `/admin/manga/${savedId}/upload`)
      },
      onError: (err) => setFormError(getErrMsg(err, 'Ошибка при сохранении')),
    })
  }

  const handleDelete = () => {
    deleteManga(id, {
      onSuccess: () => { toast.success('Манга удалена'); navigate('/admin') },
      onError: () => toast.error('Ошибка при удалении'),
    })
  }

  if (isEdit && isError && error?.response?.status === 404) return <NotFound />

  const isPending = creating || updating

  const isDirty = !isEdit || coverFile !== null || (!!manga && (
    form.title.trim()          !== (manga.title          || '') ||
    form.title_en.trim()       !== (manga.title_en       || '') ||
    form.title_original.trim() !== (manga.title_original || '') ||
    form.slug.trim()           !== (manga.slug           || '') ||
    form.author.trim()         !== (manga.author         || '') ||
    String(form.year)          !== String(manga.year     || '') ||
    form.type                  !== (manga.type           || '') ||
    form.status                !== (manga.status         || 'ongoing') ||
    form.age_rating            !== ((meta?.age_ratings ?? []).find((r) => r.name === manga.age_rating)?.code || '') ||
    form.description.trim()    !== (manga.description    || '') ||
    JSON.stringify([...form.genres].sort()) !== JSON.stringify([...(manga.genres || []).map(g => g.code ?? g)].sort())
  ))

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/admin" className="text-sm text-muted hover:text-accent transition-colors shrink-0">← Админ</Link>
          <span className="text-line shrink-0">/</span>
          <h1 className="text-xl font-bold truncate">{isEdit ? (manga?.title || 'Редактировать') : 'Новая манга'}</h1>
        </div>
        {isEdit && (
          <div className="flex items-center gap-2 shrink-0">
            {manga?.slug && (
              <a
                href={`/manga/${manga.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all
                  ${manga.is_published
                    ? 'border-line text-fg hover:border-accent hover:text-accent'
                    : 'border-line text-muted/40 cursor-not-allowed pointer-events-none'
                  }`}
                title={!manga.is_published ? 'Манга не опубликована' : undefined}
              >
                На сайте ↗
              </a>
            )}
            <Link to={`/admin/manga/${id}/upload`}
              className="px-4 py-2 rounded-xl text-sm font-bold transition-all hover:opacity-90 bg-accent text-on-accent">
              + Загрузить главу
            </Link>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* Cover */}
        <div className="flex gap-5 items-start">
          <div className="shrink-0 rounded-lg overflow-hidden bg-surface2 border border-line"
            style={{ width: 100, aspectRatio: '2/3' }}>
            {coverPreview
              ? <img src={coverPreview} alt="Обложка" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-2xl text-muted">📖</div>
            }
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">Обложка</p>
            <label className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-all bg-surface2 text-fg border border-line hover:border-accent">
              Выбрать файл
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleCover} />
            </label>
            <p className="text-xs text-muted">JPG, PNG или WebP</p>
          </div>
          {isEdit && manga && (
            <div className="ml-auto flex flex-col items-end gap-1">
              <span className="text-xs text-muted">Источник</span>
              {manga.is_mangadex
                ? <span className="text-xs px-1.5 py-0.5 rounded bg-blue-400/15 text-blue-400">MangaDex</span>
                : <span className="text-xs px-1.5 py-0.5 rounded bg-purple-400/15 text-purple-400">Admin</span>
              }
            </div>
          )}
        </div>

        <Input label="Название *" value={form.title} onChange={set('title')} placeholder="Название манги" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Название EN" value={form.title_en} onChange={set('title_en')} placeholder="English title" />
          <Input label="Оригинальное название" value={form.title_original} onChange={set('title_original')} placeholder="原題 / 원제 / ..." />
        </div>

        {isEdit && (
          <div className="flex flex-col gap-1">
            <Input label="Slug (URL)" value={form.slug} onChange={set('slug')} placeholder="my-manga-slug" />
            <p className="text-xs text-muted">
              Текущий адрес: /manga/<strong>{form.slug || manga?.slug}</strong> — меняй только осознанно
            </p>
          </div>
        )}

        <Input label="Автор" value={form.author} onChange={set('author')} placeholder="Имя автора" />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Input label="Год" type="text" inputMode="numeric" maxLength={4} pattern="[0-9]*"
            value={form.year} onChange={set('year')} placeholder={String(CURRENT_YEAR)} />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-muted">Тип</label>
            <select value={form.type} onChange={set('type')} className={selectCls}>
              {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value} style={{ background: '#1e1e1e' }}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-muted">Статус</label>
            <select value={form.status} onChange={set('status')} className={selectCls}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value} style={{ background: '#1e1e1e' }}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-muted">Возраст</label>
            <select value={form.age_rating} onChange={set('age_rating')} className={selectCls}>
              {AGE_RATING_OPTIONS.map((o) => <option key={o.value} value={o.value} style={{ background: '#1e1e1e' }}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* Genres */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-muted">
              Жанры{form.genres.length > 0 && <span className="ml-2 font-normal text-accent">{form.genres.length} выбрано</span>}
            </label>
                    <button type="button" onClick={() => { setShowGenreInput(true); setTimeout(() => newGenreRef.current?.focus(), 0) }}
              className="text-xs text-muted hover:text-accent transition-colors">
              + Новый жанр
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {GENRES.map((g) => {
              const active = form.genres.includes(g.code)
              return (
                <button key={g.code} type="button"
                  onClick={() => setForm((f) => ({
                    ...f,
                    genres: active ? f.genres.filter((x) => x !== g.code) : [...f.genres, g.code],
                  }))}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all border
                    ${active ? 'bg-accent text-on-accent border-accent' : 'bg-surface2 text-muted border-line hover:border-accent'}`}>
                  {g.name}
                </button>
              )
            })}
            {showGenreInput && (
              <div className="flex items-center gap-1">
                <input
                  ref={newGenreRef}
                  value={newGenre}
                  onChange={(e) => setNewGenre(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleAddGenre() }
                    if (e.key === 'Escape') { setNewGenre(''); setShowGenreInput(false) }
                  }}
                  placeholder="Название..."
                  className="px-3 py-1 rounded-full text-xs outline-none bg-surface2 border border-accent text-fg w-32 placeholder:text-muted"
                />
                <button type="button" onClick={handleAddGenre} disabled={creatingGenre || !newGenre.trim()}
                  className="px-2 py-1 rounded-full text-xs font-medium bg-accent text-on-accent disabled:opacity-50">
                  {creatingGenre ? '...' : 'OK'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-muted">Описание</label>
          <textarea value={form.description} onChange={set('description')} placeholder="Описание манги..." rows={4}
            className="px-4 py-3 rounded-lg text-sm resize-none outline-none transition-colors bg-surface2 border border-line text-fg focus:border-accent"
            style={{ fontFamily: 'var(--font)' }}
          />
        </div>

        {formError && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm bg-red-400/10 border border-red-400/25 text-red-400">
            <span>⚠</span><span>{formError}</span>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <div className="flex gap-3">
            <button type="submit" disabled={isPending || !isDirty}
              className="px-6 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50 hover:opacity-90 bg-accent text-on-accent">
              {isPending ? 'Сохранение...' : isEdit ? 'Сохранить' : 'Создать'}
            </button>
            <Link to="/admin"
              className="px-6 py-2.5 rounded-xl text-sm font-semibold border border-line text-muted hover:text-fg hover:border-accent transition-all">
              Отмена
            </Link>
          </div>
          {isEdit && (
            <>
              <button type="button" onClick={() => setDeleteConfirm(true)}
                className="text-sm text-muted hover:text-red-400 transition-colors">
                Удалить мангу
              </button>
              <ConfirmModal
                isOpen={deleteConfirm}
                onClose={() => !deleting && setDeleteConfirm(false)}
                onConfirm={handleDelete}
                title={`Удалить «${manga?.title}»?`}
                message="Все главы, страницы и обложка будут удалены безвозвратно."
                confirmLabel="Да, удалить"
                isPending={deleting}
                danger
              />
            </>
          )}
        </div>
      </form>

      {/* Chapter list */}
      {isEdit && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold">
              Главы {chapters.length > 0 && <span className="text-muted font-normal">({chapters.length})</span>}
            </h2>
            <Link to={`/admin/manga/${id}/upload`} className="text-sm font-semibold text-accent">
              + Добавить главу
            </Link>
          </div>

          {loadingChapters ? (
            <p className="text-sm py-4 text-center text-muted">Загрузка...</p>
          ) : chapters.length === 0 ? (
            <div className="py-8 text-center rounded-xl border border-dashed border-line text-muted">
              <p className="text-sm mb-3">Глав пока нет</p>
              <Link to={`/admin/manga/${id}/upload`} className="text-sm font-semibold text-accent">
                Загрузить первую главу →
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {showVolumeHeaders
                ? volumes.map((vol) => {
                    const volChapters = [...chapters]
                      .filter((ch) => ch.volume_id === vol.id)
                      .sort((a, b) => sortDesc ? b.number - a.number : a.number - b.number)
                    if (!volChapters.length) return null
                    return (
                      <div key={vol.id}>
                        <p className="text-xs font-bold uppercase tracking-wider px-1 pt-3 pb-1 text-accent">
                          Том {vol.number}{vol.title ? ` — ${vol.title}` : ''}
                        </p>
                        {volChapters.map((ch) => <ChapterRow key={ch.id} ch={ch} manga={manga} mangaId={id} />)}
                      </div>
                    )
                  })
                : [...chapters]
                    .sort((a, b) => sortDesc ? b.number - a.number : a.number - b.number)
                    .map((ch) => <ChapterRow key={ch.id} ch={ch} manga={manga} mangaId={id} />)
              }
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ChapterRow({ ch, manga, mangaId }) {
  const { mutate: deleteChapter, isPending } = useDeleteChapter(mangaId)
  const [confirm, setConfirm] = useState(false)

  return (
    <>
      <div className="flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors bg-surface hover:bg-surface2">
        <Link to={`/manga/${manga?.slug || mangaId}/chapter/${ch.number}`} className="flex-1 text-sm">
          Глава {ch.number}{ch.title ? ` — ${ch.title}` : ''}
          <span className="ml-2 text-xs text-muted">{ch.pages_count} стр.</span>
        </Link>
        <button onClick={() => setConfirm(true)}
          className="ml-3 text-xs px-2 py-1 rounded transition-colors shrink-0 text-muted hover:text-red-400">
          Удалить
        </button>
      </div>
      <ConfirmModal
        isOpen={confirm}
        onClose={() => setConfirm(false)}
        onConfirm={() => { deleteChapter({ volumeId: ch.volume_id, chapterId: ch.id }); setConfirm(false) }}
        title={`Удалить главу ${ch.number}?`}
        message="Все страницы главы будут удалены безвозвратно."
        confirmLabel="Да, удалить"
        isPending={isPending}
        danger
      />
    </>
  )
}
